import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, BulletList, BulletListItem, Column, ConfirmationModal, Row, Spinner, Typography } from 'seagull';
import { Checkmark, ExclamationMark, Info } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStoreActions } from '~/contexts';
import { useMutationState } from '~/hooks/reactQuery/useMutationState';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import styles from './ClientsBulkActionResultModal.module.scss';
export const GetClientsBulkActionResultModalTID = (parentTestId)=>getTestIds(parentTestId);
export const ClientsBulkActionResultModal = (param)=>{
    let { actionType, ids, onClose, onComplete } = param;
    const { api } = useStoreActions();
    const { t: bulkActionResultTexts } = useLocaleKeys('client.dashboard.bulkActionResult');
    const { ClientsBulkActionResultModalTID: TID } = useMemo(()=>GetClientsBulkActionResultModalTID(), []);
    const { mutate: sendBulkRequest, data: result, isPending: isBulkInProgress, isError } = useMutationState({
        mutationFn: ()=>actionType === 'archive' ? api.client.bulkArchive({
                ids
            }) : api.client.bulkDelete({
                ids
            })
    });
    useOnMount(()=>{
        sendBulkRequest();
    });
    const handleClose = ()=>{
        if (!isError) {
            onComplete({
                actionType,
                succeededIds: ids.filter((id)=>!(result === null || result === void 0 ? void 0 : result.failures.some((failure)=>failure.id === id)))
            });
        }
        onClose();
    };
    const renderFailedSection = (clients, message, testId)=>clients.length > 0 && /*#__PURE__*/ _jsxs(Column, {
            className: styles.errorSection,
            children: [
                /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "start",
                    gap: 8,
                    children: [
                        /*#__PURE__*/ _jsx(ExclamationMark, {
                            size: 18
                        }),
                        ' ',
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            testId: testId,
                            children: message
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(BulletList, {
                    spacing: "compact",
                    className: styles.failedClientsList,
                    children: clients.map((client)=>/*#__PURE__*/ _jsx(BulletListItem, {
                            custom: true,
                            bulletSize: "small",
                            className: styles.failedClientItem,
                            testId: TID.FailedClient(client.id),
                            children: /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph3",
                                weight: "bold",
                                children: client.name || bulkActionResultTexts.unknownClient()
                            })
                        }, client.id))
                })
            ]
        });
    const renderResult = (bulkResult)=>{
        const blockedClients = bulkResult.failures.filter((failed)=>failed.type === 'blocked');
        const erroredClients = bulkResult.failures.filter((failed)=>failed.type === 'error');
        return /*#__PURE__*/ _jsxs(Column, {
            children: [
                bulkResult.successful > 0 && /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    gap: 8,
                    className: styles.successSection,
                    children: [
                        /*#__PURE__*/ _jsx(Checkmark, {}),
                        ' ',
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            testId: TID.SucceededText(),
                            children: bulkActionResultTexts.successMessage({
                                count: bulkResult.successful,
                                actionType
                            })
                        })
                    ]
                }),
                bulkResult.skipped > 0 && /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    gap: 8,
                    className: styles.skippedSection,
                    children: [
                        /*#__PURE__*/ _jsx(Info, {}),
                        ' ',
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            testId: TID.SkippedText(),
                            children: bulkActionResultTexts.skippedMessage({
                                count: bulkResult.skipped,
                                actionType
                            })
                        })
                    ]
                }),
                renderFailedSection(blockedClients, bulkActionResultTexts.failureMessage({
                    count: blockedClients.length,
                    actionType
                }), TID.FailedText()),
                renderFailedSection(erroredClients, bulkActionResultTexts.errorMessage({
                    count: erroredClients.length,
                    actionType
                }), TID.ErroredText())
            ]
        });
    };
    const renderContent = ()=>{
        if (isBulkInProgress) {
            return /*#__PURE__*/ _jsx(Box, {
                className: styles.loadingContainer,
                children: /*#__PURE__*/ _jsx(Spinner, {
                    testId: TID.Spinner()
                })
            });
        }
        if (isError) {
            return /*#__PURE__*/ _jsx(Column, {
                className: styles.errorSection,
                children: /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "start",
                    gap: 4,
                    children: [
                        /*#__PURE__*/ _jsx(ExclamationMark, {
                            size: 18
                        }),
                        ' ',
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            testId: TID.RequestErrorText(),
                            children: bulkActionResultTexts.requestErrorMessage()
                        })
                    ]
                })
            });
        }
        if (result) {
            return renderResult(result);
        }
        return null;
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'clients_bulk_action_result_confirm'
            },
            secondaryButton: {
                name: 'clients_bulk_action_result_cancel'
            }
        },
        title: bulkActionResultTexts.title[actionType](),
        textAlignCenter: false,
        width: "680px",
        maxHeight: "700px",
        secondaryButtonLabel: bulkActionResultTexts.closeButton(),
        secondaryButtonTestId: TID.Close(),
        cancelButtonDisabled: isBulkInProgress,
        onSecondaryButtonClick: handleClose,
        onCloseButtonClick: handleClose,
        content: renderContent()
    });
};
